import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Globe, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BookingSettings, SpecialDates, VacationCard, WeeklyEditorDialog, WeeklySummary } from "@lawyer/components/availability/AvailabilitySections";
import { OverrideDrawer } from "@lawyer/components/availability/OverrideDrawer";
import { sameWeekly } from "@lawyer/components/availability/scheduleUtils";

const STATE = {
  online_scheduled: ["Online · Scheduled", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  online_manual: ["Online · Manual", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  busy: ["Busy", "bg-amber-50 text-amber-800 border-amber-200"],
  offline: ["Offline", "bg-slate-100 text-slate-600 border-slate-200"],
  vacation: ["On vacation", "bg-amber-50 text-amber-800 border-amber-200"],
};
const MODE = { chat: "Chat", audio: "Audio", video: "Video" };
const bookingLine = (b) => `${b.slot_date_ist || b.slot_date} · ${b.slot_time_ist || b.slot_time} IST · ${MODE[b.consultation_type] || b.consultation_type}${b.user_name ? ` · ${b.user_name}` : ""}`;

export default function LawyerAvailability({ data, onReload, onVacationToggle }) {
  const [saved, setSaved] = useState(null);       // settings from server
  const [weekly, setWeekly] = useState(null);     // unsaved grid state
  const [notice, setNotice] = useState(120);
  const [horizon, setHorizon] = useState(14);
  const [overrides, setOverrides] = useState([]);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pickedDate, setPickedDate] = useState(null);
  const [confirm, setConfirm] = useState(null);   // { title, affected, onConfirm }
  const [editing, setEditing] = useState(false);  // weekly editor dialog

  const load = useCallback(async () => {
    const [s, o, st] = await Promise.all([
      api.get("/lawyer/availability/settings"), api.get("/lawyer/availability/overrides"), api.get("/lawyer/availability/status"),
    ]);
    setSaved(s.data); setWeekly(s.data.weekly); setNotice(s.data.min_notice_minutes); setHorizon(s.data.horizon_days);
    setOverrides(o.data.items || []); setStatus(st.data);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Could not load availability")); }, [load]);

  const dirty = useMemo(() => !!saved && !!weekly && (!sameWeekly(weekly, saved.weekly) || notice !== saved.min_notice_minutes || horizon !== saved.horizon_days), [saved, weekly, notice, horizon]);

  useEffect(() => {
    document.body.dataset.unsaved = dirty ? "1" : "";
    const warn = (e) => { if (document.body.dataset.unsaved === "1" || document.body.dataset.unsavedOverride === "1") { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => { window.removeEventListener("beforeunload", warn); document.body.dataset.unsaved = ""; };
  }, [dirty]);

  const persistWeekly = async (w = weekly) => {
    setSaving(true);
    try {
      const r = await api.put("/lawyer/availability/settings", { weekly: w, min_notice_minutes: notice, horizon_days: horizon });
      setSaved(r.data); setWeekly(r.data.weekly);
      toast.success("Availability saved");
      setStatus((await api.get("/lawyer/availability/status")).data);
      onReload?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };
  const saveWeekly = async (w = weekly) => {
    setSaving(true);
    try {
      const body = { weekly: w, min_notice_minutes: notice, horizon_days: horizon };
      const dry = await api.put("/lawyer/availability/settings?dry_run=true", body);
      const affected = dry.data.affected_bookings || [];
      if (affected.length) { setSaving(false); setWeekly(w); setEditing(false); setConfirm({ title: "Save weekly schedule?", affected, onConfirm: () => persistWeekly(w) }); return; }
      await persistWeekly(w);
      setEditing(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); setSaving(false); }
  };
  const discard = () => { setWeekly(saved.weekly); setNotice(saved.min_notice_minutes); setHorizon(saved.horizon_days); };

  const persistOverride = async ({ date, windows, reason }) => {
    setSaving(true);
    try {
      await api.put(`/lawyer/availability/overrides/${date}`, { windows, reason });
      setOverrides((await api.get("/lawyer/availability/overrides")).data.items || []);
      toast.success(windows.length ? `Custom hours saved for ${date}` : `${date} marked as day off`);
      setPickedDate(null);
      setStatus((await api.get("/lawyer/availability/status")).data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save override"); } finally { setSaving(false); }
  };
  const saveOverride = async (payload) => {
    setSaving(true);
    try {
      const dry = await api.put(`/lawyer/availability/overrides/${payload.date}?dry_run=true`, { windows: payload.windows });
      const affected = dry.data.affected_bookings || [];
      if (affected.length) { setSaving(false); setConfirm({ title: payload.windows.length ? "Save custom hours?" : "Mark this day off?", affected, onConfirm: () => persistOverride(payload) }); return; }
      await persistOverride(payload);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save override"); setSaving(false); }
  };
  const removeOverride = async (date) => {
    try {
      await api.delete(`/lawyer/availability/overrides/${date}`);
      setOverrides((prev) => prev.filter((o) => o.date !== date));
      setPickedDate(null);
      toast.success("Override removed — weekly schedule applies again");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove override"); }
  };

  const vacationOn = !!data?.lawyer?.vacation_mode;
  const askVacation = async () => {
    if (vacationOn) { await onVacationToggle?.(); setStatus((await api.get("/lawyer/availability/status")).data); return; }
    const st = (await api.get("/lawyer/availability/status")).data;
    setConfirm({
      title: "Turn on vacation mode?", vacation: true, affected: st.upcoming_bookings || [], count: st.upcoming_bookings_count || 0,
      onConfirm: async () => { await onVacationToggle?.(); setStatus((await api.get("/lawyer/availability/status")).data); },
    });
  };

  if (!weekly) return <div className="grid place-items-center py-24 text-slate-400"><Loader2 className="animate-spin" /></div>;
  const [stLabel, stCls] = STATE[status?.state] || STATE.offline;

  return (
    <div className="space-y-6" data-testid="availability-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0A2540]">Availability</h1>
          <p className="text-sm text-slate-500 mt-1">When clients can book you.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold rounded-full border px-2.5 py-1 inline-flex items-center gap-1 bg-white border-slate-200 text-slate-600" data-testid="tz-badge"><Globe size={11} /> IST · Asia/Kolkata</span>
          <span className={`text-[11px] font-bold rounded-full border px-2.5 py-1 ${stCls}`} data-testid="right-now" data-state={status?.state || ""}>Right now: {stLabel}</span>
        </div>
      </div>

      {dirty && (
        <div className="sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="unsaved-bar">
          <span className="text-sm font-extrabold text-amber-900" data-testid="unsaved-badge">Unsaved changes</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={discard} disabled={saving} data-testid="discard-changes" className="bg-white">Discard</Button>
            <Button onClick={() => saveWeekly()} disabled={saving} data-testid="save-weekly" className="bg-[#0A2540] text-white hover:bg-[#0A2540]/90">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} className="mr-1" /> Save changes</>}
            </Button>
          </div>
        </div>
      )}

      <WeeklySummary weekly={weekly} onEdit={() => setEditing(true)} />
      <SpecialDates overrides={overrides} weekly={saved?.weekly || weekly} onPick={setPickedDate} onRemove={removeOverride} />
      <div className="grid lg:grid-cols-2 gap-6">
        <BookingSettings notice={notice} horizon={horizon} onNotice={setNotice} onHorizon={setHorizon} />
        <VacationCard on={vacationOn} upcomingCount={status?.upcoming_bookings_count || 0} onToggle={askVacation} />
      </div>

      {editing && <WeeklyEditorDialog open weekly={weekly} saving={saving} onCancel={() => setEditing(false)} onSave={saveWeekly} />}

      {pickedDate && (
        <OverrideDrawer key={pickedDate} date={pickedDate} weekly={saved?.weekly || weekly} existing={overrides.find((o) => o.date === pickedDate) || null}
          onClose={() => setPickedDate(null)} onSave={saveOverride} onRemove={removeOverride} saving={saving} />
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent data-testid="affected-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-slate-600 space-y-2">
                <p data-testid="affected-summary">
                  {confirm?.vacation
                    ? (confirm.count ? `You have ${confirm.count} upcoming paid consultation${confirm.count > 1 ? "s" : ""}. Vacation mode will not cancel them — they remain booked and you are expected to attend or cancel them yourself.` : "No upcoming consultations. New bookings will be paused until you end vacation mode.")
                    : `You have ${confirm?.affected.length} upcoming consultation${confirm?.affected.length > 1 ? "s" : ""} in the hours you're removing. These consultations will remain booked.`}
                </p>
                {!!confirm?.affected?.length && (
                  <ul className="text-xs bg-slate-50 rounded-lg p-2 space-y-1 max-h-40 overflow-y-auto" data-testid="affected-list">
                    {confirm.affected.slice(0, 10).map((b) => <li key={b.id}>{bookingLine(b)}</li>)}
                    {confirm.affected.length > 10 && <li>…and {confirm.affected.length - 10} more</li>}
                  </ul>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="affected-cancel">Go back</AlertDialogCancel>
            <AlertDialogAction data-testid="affected-confirm" onClick={(e) => { e.preventDefault(); const fn = confirm.onConfirm; setConfirm(null); fn(); }} className="bg-[#0A2540] hover:bg-[#0A2540]/90">
              {confirm?.vacation ? "Turn on vacation" : "Save anyway"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
